/* eslint-disable jsx-a11y/label-has-for,react/no-danger */
import React, { Fragment, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { size, flow, sortBy, identity, map } from 'lodash/fp';
import { formatPrice } from '../../../helpers/formatNumbers';
import { classNames, boolClass } from '../../../helpers/classNameHelpers';
import InstallationSummaryModal from '../../InstallationSummaryModal';
import InstallationOnlyModal from '../../InstallationOnlyModal';
import { useModalProviderContext } from '../../ModalProvider';
import { installationSummaryModal, installationOnlyModal } from '../../ModalProvider/modalNames';
import InstallationPricing from '../../InstallationPricing';
import cookiesFactory from '../../../../../base/cookies-factory';
import useMedia from '../../../hooks/useMedia';
import { breakpointsDesktopFirst } from '../../../themes/breakpoints';

import addProductOnlyToCart from './addProductOnlyToCart';
import sendAnalyticsEvent from '../../InstallationSummaryModal/sendAnalyticsEvent';
import getAttributeCount from '../../../helpers/getAttributeCount';
import BuyWithAddToOrderButton from '../AddToOrder/BuyWithAddToOrderButtonConnector';


const isInternalUser = cookiesFactory.isInternalUser();

const InstallationSummary = ({
  excludeCharges,
  retail,
  tax,
  subtotal,
  name,
  locale,
  selectedWindows,
  totalInstallationCost,
  isLinked,
  isProductOnly,
  disableDiyCheckout,
  disableDiyAddToOrder,
  className,
  promo,
  designId,
  updateInstallationPricing,
  calculateTax,
  calculateTaxModel,
  selectedInstallWindows,
  isAutoReleaseDesign,
}) => {
  const discountTotal = retail - subtotal;
  const subTotalWithoutDiscount = retail;
  const totalWithoutDiscount = subTotalWithoutDiscount + tax;
  const {
    setIsOpen: setInstallationSummaryModalOpen,
    isOpen: isSummaryModalOpen,
  } = useModalProviderContext(installationSummaryModal);

  const selectedWindowsHash = JSON.stringify(flow(
    map('itemId'),
    sortBy(identity),
  )(selectedWindows));

  const haulAwayCount = getAttributeCount('windowHaul', selectedInstallWindows);
  const removalCount = getAttributeCount('windowRemoval', selectedInstallWindows);
  const adjustmentCount = getAttributeCount('windowAdjustment', selectedInstallWindows) || getAttributeCount('windowadjustment', selectedInstallWindows);

  const {
    setIsOpen: setInstallationOnlyModalOpen,
    isOpen: isInstallOnlyModalOpen,
  } = useModalProviderContext(installationOnlyModal);

  const promoMessage = () => {
    let text = `${locale.promoCodeText.replace(
      '{{promoCode}}',
      promo.code,
    )}`;
    if (promo.endDate) {
      text += ` ${locale.promoEndDateText.replace(
        '{{promoEndDate}}',
        promo.endDate,
      )}`;
    }
    return text;
  };

  const subtotalWithInstall = subtotal + totalInstallationCost;

  const isMobile = useMedia(breakpointsDesktopFirst.tablet);

  const checkout = useCallback((e) => {
    e.preventDefault();

    if (isProductOnly) {
      addProductOnlyToCart(designId, selectedWindows);
    } else {
      setInstallationSummaryModalOpen(true);
      sendAnalyticsEvent();
    }
  }, [isProductOnly, setInstallationSummaryModalOpen, selectedWindowsHash]);

  useEffect(() => {
    if (!isSummaryModalOpen && !isInstallOnlyModalOpen) {
      const options = {};
      if (removalCount || haulAwayCount || adjustmentCount) {
        if (haulAwayCount) {
          options.windowHaul = !!haulAwayCount;
          options.windowHaulCount = haulAwayCount;
        }
        if (removalCount) {
          options.windowRemoval = !!removalCount;
          options.windowRemoveCount = removalCount;
        }
        if (adjustmentCount) {
          options.windowAdjustment = !!adjustmentCount;
          options.windowAdjustmentCount = adjustmentCount;
        }
        updateInstallationPricing(designId, map('itemId', selectedWindows), options, { mode: 'transient' });
      }
    }
  }, [selectedWindowsHash, selectedInstallWindows.length]);

  // need to filter out the product only from selected windows here
  const checkoutInstallations = useCallback((e) => {
    e.preventDefault();
    setInstallationOnlyModalOpen(true);
  }, [setInstallationOnlyModalOpen, selectedWindowsHash]);

  useEffect(() => {
    calculateTax(calculateTaxModel, designId);
  }, []);

  return (
    <React.Fragment>
      <InstallationSummaryModal designId={designId} />
      <InstallationOnlyModal designId={designId} />
      <div className={classNames(['estimateSummary', className])}>
        <div className="pricingHeader">
          <span
            className="estimateName truncateText"
            title={`${name} ${locale.summaryHeader}`}
          >
            {name}
          </span>
          <span className="summary">{locale.summaryHeader}</span>
        </div>
        <div className="installationPricingDetails">
          <div className="summaryTotals">
            {!isAutoReleaseDesign && (
            <div className="pricingLine">
              <label className="name">{locale.selectedWindows}</label>
              <span className="total" id="selectedWindows">{size(selectedWindows)}</span>
            </div>
            )}
            <div className="pricingLine">
              <label className="name">{locale.productTotal}</label>
              <span className="total" id="windowTotal">{formatPrice(retail)}</span>
            </div>
            {!isMobile && !isAutoReleaseDesign &&
              <InstallationPricing designId={designId} excludeCharges={excludeCharges} />
            }
            <div className="pricingLine">
              <label className="name">{locale.shipping}</label>
              <span className="total">{locale.shippingFree}</span>
            </div>
            {discountTotal > 0 && !isAutoReleaseDesign && (
              <div className="youllSave">
                <label className="name">{locale.youllSave}</label>
                <span className="total" id="discountTotal">{formatPrice(discountTotal)}</span>
              </div>
            )}
            <div className={isAutoReleaseDesign ? 'subTotalWithTax' : 'subTotal'}>
              <label>{locale.subtotal}</label>
              <span id="subtotalWithInstall">{isAutoReleaseDesign ? formatPrice(subTotalWithoutDiscount) : formatPrice(subtotalWithInstall)}</span>
            </div>
            <div className="subTotalMobile">
              <label>{locale.pretaxTotal}</label>
              <span id="subtotalWithInstallMobile">{formatPrice(subtotalWithInstall)}</span>
            </div>
            {isAutoReleaseDesign && (
              <div className="pricingLine">
                <label className="name">{locale.orderTax}</label>
                <span className="total">{tax === 0 ? locale.taxTotal : formatPrice(tax)}</span>
              </div>
            )}
            {isAutoReleaseDesign && (
            <div className="subTotal largerPricingText">
              <label>{locale.total}</label>
              <span>{tax === 0 ? locale.taxTotal : formatPrice(totalWithoutDiscount)}</span>
            </div>
            )}
            <div className="summaryDetails tablet-mobile-only">
              <span>
                <a
                  href="#details"
                  onClick={checkout}
                >
                View Summary
                </a>
              </span>
            </div>
          </div>

          <div className={classNames(['summaryActions', boolClass(isLinked, 'isSaved')])}>
            {isAutoReleaseDesign
              ?
                isInternalUser && (
                <div className="summaryAction buy-diy">
                  <BuyWithAddToOrderButton
                    text={locale.addToOrder}
                    textMobile={locale.addToOrder}
                    disabled={disableDiyAddToOrder}
                    designId={designId}
                    calculateTaxModel={calculateTaxModel}
                  />
                </div>
                )
              :
                <Fragment>
                  <div className="summaryAction buy-diy">
                    <div>
                      <button
                        className="btn btn-primary"
                        onClick={checkout}
                        disabled={disableDiyCheckout}
                      >
                        <span>Checkout</span>
                      </button>
                    </div>
                  </div>
                  {isInternalUser && (
                  <div className="summaryAction buy-diy">
                    <div>
                      <button
                        id="installationOnlyButton"
                        className="btn btn-secondary-base-border"
                        onClick={checkoutInstallations}
                      >
                        <span>Installation Only</span>
                      </button>
                    </div>
                  </div>
                )}
                </Fragment>
            }
          </div>
        </div>

        {!isAutoReleaseDesign && promo.code && (
          <div className="promo-message">
            <span dangerouslySetInnerHTML={{ __html: promoMessage() }} />
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

InstallationSummary.propTypes = {
  excludeCharges: PropTypes.arrayOf(PropTypes.string),
  name: PropTypes.string.isRequired,
  subtotal: PropTypes.number,
  retail: PropTypes.number.isRequired,
  tax: PropTypes.number,
  disableDiyCheckout: PropTypes.bool.isRequired,
  disableDiyAddToOrder: PropTypes.bool.isRequired,
  selectedWindows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  totalInstallationCost: PropTypes.number.isRequired,
  className: PropTypes.string,
  isLinked: PropTypes.bool,
  promo: PropTypes.shape({
    code: PropTypes.string,
    endDate: PropTypes.string,
  }),
  locale: PropTypes.shape({
    summaryHeader: PropTypes.string.isRequired,
    orderTax: PropTypes.string.isRequired,
    shipping: PropTypes.string.isRequired,
    shippingFree: PropTypes.string.isRequired,
    pretaxTotal: PropTypes.string.isRequired,
    subtotal: PropTypes.string.isRequired,
    youllSave: PropTypes.string.isRequired,
    selectedWindows: PropTypes.string.isRequired,
    productTotal: PropTypes.string.isRequired,
    promoCodeText: PropTypes.string.isRequired,
    promoEndDateText: PropTypes.string.isRequired,
  }).isRequired,
  designId: PropTypes.string.isRequired,
  isProductOnly: PropTypes.bool.isRequired,
  updateInstallationPricing: PropTypes.func.isRequired,
  selectedInstallWindows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isAutoReleaseDesign: PropTypes.bool.isRequired,
  calculateTax: PropTypes.func.isRequired,
  calculateTaxModel: PropTypes.shape({
    customerEmail: PropTypes.string.isRequired,
    orderNumber: PropTypes.string.isRequired,
    siteId: PropTypes.number.isRequired,
    shippingAddress: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      address1: PropTypes.string.isRequired,
      address2: PropTypes.string,
      city: PropTypes.string.isRequired,
      province: PropTypes.string.isRequired,
      postalCode: PropTypes.string.isRequired,
      phone: PropTypes.string.isRequired,
      country: PropTypes.string.isRequired,
    }).isRequired,
    lineItems: PropTypes.arrayOf(PropTypes.shape({
      number: PropTypes.number.isRequired,
      amount: PropTypes.number.isRequired,
      quantity: PropTypes.number.isRequired,
      productId: PropTypes.number.isRequired,
      productName: PropTypes.string.isRequired,
      discounted: PropTypes.bool.isRequired,
      lineItemGroupGuid: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
};

InstallationSummary.defaultProps = {
  className: '',
  isLinked: false,
  subtotal: 0,
  tax: 0,
  promo: {
    code: null,
    endDate: null,
  },
  excludeCharges: [],
};

export default InstallationSummary;
